import { SITE_LOCALES, SiteLocale } from '../nav-link';

/**
 * Which language, if any, to offer the reader.
 *
 * Pure on purpose. The component that renders the banner only decides *when*
 * to ask; this decides *what the answer is*, so it can be tested against a
 * list of browser languages without a DOM, a build, or a screenshot.
 *
 * The rules, in order:
 *
 * 1. If the reader dismissed the offer before, never ask again.
 * 2. Walk `navigator.languages` in the reader's own order of preference and
 *    take the first one this site actually has. A browser sending
 *    `['de-AT', 'de', 'en']` gets German, because `de-AT` matches `de` on the
 *    primary subtag.
 * 3. If that language is the page they are already on, say nothing. This is
 *    the case that matters most: it means someone who arrived on `/fr/`, or
 *    who picked French from the header, is never nagged.
 * 4. If their top preference is one we do not have, say nothing rather than
 *    offering the next best guess. A Portuguese speaker reading the English
 *    page is not helped by being offered Spanish.
 */
export function pickSuggestion(
  languages: readonly string[],
  currentLocale: string,
  dismissed: boolean
): SiteLocale | null {
  if (dismissed) return null;

  for (const tag of languages) {
    const primary = tag.toLowerCase().split('-')[0];
    const match = SITE_LOCALES.find((l) => l.code === primary);

    // The first tag we recognise decides it, one way or the other. Reading
    // past it would offer a language the reader ranked below the page they
    // are already looking at.
    if (match) return match.code === currentLocale ? null : match;
  }

  return null;
}

/**
 * The same route, in the other language.
 *
 * `/privacy` on English becomes `/fr/privacy`, and `/fr/privacy` becomes
 * `/de/privacy`. Sending someone from a deep page back to a home page is a
 * small betrayal of the click, and this site has three routes where it would
 * happen.
 */
export function localisedPath(
  pathname: string,
  currentLocale: string,
  target: SiteLocale
): string {
  const currentPrefix = currentLocale === 'en' ? '' : `/${currentLocale}`;
  const route = currentPrefix
    ? pathname.slice(currentPrefix.length) || '/'
    : pathname;

  const targetPrefix = target.code === 'en' ? '' : `/${target.code}`;

  // A locale home is `/fr`, never `/fr/`: the slashed form 301s under
  // `trailingSlash: false`, and sending the reader through a redirect to
  // change language is a redirect we control and can simply not cause.
  if (route === '/') return targetPrefix || '/';

  const joined = `${targetPrefix}${route}`;
  return joined.startsWith('/') ? joined : `/${joined}`;
}
